import { useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";
import CustomText from "components/common/CustomText/CustomText";
import SearchIcon from "icons/SearchIcon";
import { colors } from "styles/colors";
import { useSearchUsers } from "hooks/useUsers";
import useGlobalStyles from "styles/globalStyles";
import { useFormContext } from "react-hook-form";
import { ERegistryPermission } from "enum/ERegistryPermission";
import { RegistryUser } from "types/types";
import CloseIcon from '@mui/icons-material/Close';
import { hexWithOpacity } from "common/utils";
import UsersIconOutline from "icons/UsersIconOutline";

export default function FormShareRegistry() {
  const globalStyles = useGlobalStyles();
  const [keyword, setKeyword] = useState<string>('');

  const { data: users, isLoading, isFetching } = useSearchUsers(keyword, {
    keepPreviousData: true,
  });

  const {
    watch,
    setValue,
  } = useFormContext();

  const selectedUsers = watch("users") || [];

  const toggleUser = (user: any) => {
    const exists = selectedUsers.find((u: RegistryUser) => u.user_id === user.id);

    let updated;

    if (exists) {
      updated = selectedUsers.filter((u: RegistryUser) => u.user_id !== user.id);
    } else {
      updated = [
        ...selectedUsers,
        {
          user_id: user.id,
          invitee_name: user.invitee_name,
          permission: ERegistryPermission.ALL,
        },
      ];
    }

    setValue("users", updated, { shouldValidate: true });
  };


  const renderContent = () => {
    // 1️⃣ Before typing anything
    if (!keyword.trim()) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          gap={2}
          flex={1}
        >
          <UsersIconOutline
            size={80}
            color={hexWithOpacity(colors.secondary, 0.5)}
          />
          <CustomText text="Start typing to get results!" color={colors.secondary} />
        </Box>
      );
    }

    // 2️⃣ While searching
    if (isLoading || isFetching) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          gap={2}
          flex={1}
        >
          <UsersIconOutline
            size={80}
            color={hexWithOpacity(colors.secondary, 0.5)}
          />
          <CustomText text="Searching..." color={colors.secondary} />
        </Box>
      );
    }

    // 3️⃣ Search finished but nothing found
    if (users?.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          gap={2}
          flex={1}
        >
          <UsersIconOutline
            size={80}
            color={hexWithOpacity(colors.secondary, 0.5)}
          />
          <CustomText text="No invitations found" color={colors.secondary} />
        </Box>
      );
    }

    // 4️⃣ Results
    return (
      <>
        {users?.map((user: any) => {
          const checked = selectedUsers.some(
            (u: any) => u.user_id === user.id
          );

          return (
            <ListItem
              key={user.id}
              className={globalStyles.resultItem}
              onClick={() => toggleUser(user)}
              sx={{ flexShrink: 0 }}
            >
              <ListItemText primary={user.invitee_name} />

              <Checkbox
                checked={checked}
                onChange={() => toggleUser(user)}
              />
            </ListItem>
          );
        })}
      </>
    );
  };

  return (
    <Stack
      mx={3}
      p={2}
      spacing={2}
      flex={1}
      minHeight={0}
      sx={{
        border: `1px solid ${colors.secondary}`,
        borderRadius: "4px",
        maxHeight: "100%",
        overflow: "hidden"
      }}
    >
      <CustomText
        text="Share Registry"
        fontSize={16}
        color={colors.primary}
        fontWeight={600}
        lineHeight={1}
      />

      <CustomText
        text="Share registry with your colleagues!"
        fontSize={12}
        color={colors.secondary}
        lineHeight={0.5}
      />

      <CustomText
        text="Invited users will receive emails with their invitations."
        fontSize={12}
        color={colors.secondary}
        lineHeight={0.5}
      />

      <Divider />

      {/* Search */}
      <Grid container justifyContent="flex-start" sx={{ pt: 0.2 }}>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <TextField
            fullWidth
            placeholder="Search"
            size="small"
            variant="standard"
            onChange={(e) => {
              setKeyword(e.target.value);
            }}
            slotProps={{
              input: {
                disableUnderline: true,
                sx: {
                  fontSize: "15px",
                  fontWeight: 400,
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="gray" size={16} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
      </Grid>

      <Divider />

      {/* Example invited list */}
      <Box
        flex={1}
        sx={{
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={1}
          mb={1.5}
        >
          {selectedUsers?.map((user: any) => (
            <Stack
              key={user.user_id}
              direction="row"
              alignItems="center"
              sx={{
                border: `1px solid ${hexWithOpacity(colors.secondary, 0.5)}`,
                borderRadius: "8px",
                pl: 1,
              }}
            >
              <CustomText
                text={user.invitee_name}
                color={colors.black}
              />

              <IconButton onClick={() => toggleUser({ id: user.user_id })}>
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>

        {renderContent()}
      </Box>
    </Stack>
  );
}